// @CODE:PERF-002-JS Lazy Loading Infrastructure with Intersection Observer
// Purpose: Enable scroll-based lazy loading for videos and images with LQIP blur strategy
// Supports: 200px rootMargin, 0.01 threshold, IntersectionObserver fallback

class LazyLoader {
  constructor(options = {}) {
    this.options = {
      rootMargin: options.rootMargin || '200px',
      threshold: options.threshold || 0.01,
      ...options
    };

    // Feature detection: IntersectionObserver support
    if ('IntersectionObserver' in window) {
      this.initIntersectionObserver();
    } else {
      this.initFallback();
    }

    // Global singleton for deferred media coordination
    window.lazyLoader = this;
  }

  /**
   * Initialize Intersection Observer for lazy loading
   * Observes elements with data-srcset (images) and data-src (videos)
   */
  initIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target;
          if (element.dataset.src) {
            this.loadVideo(element);
          } else if (element.dataset.srcset) {
            this.loadImage(element);
          }
          this.observer.unobserve(element);
        }
      });
    }, observerOptions);
  }

  /**
   * Fallback for browsers without IntersectionObserver
   * Load all elements immediately
   */
  initFallback() {
    console.warn('IntersectionObserver not supported. Loading all lazy media immediately.');
    const lazyVideos = document.querySelectorAll('[data-src]');
    const lazyImages = document.querySelectorAll('[data-srcset]');

    lazyVideos.forEach((video) => {
      if (window.pauseAllMedia) window.pauseAllMedia();
      this.loadVideo(video);
    });

    lazyImages.forEach((image) => {
      this.loadImage(image);
    });
  }

  /**
   * Load video element
   * Clones template content for deferred-media compatibility
   */
  loadVideo(element) {
    if (element.hasAttribute('data-loaded')) return;

    try {
      // Check if element is deferred-media custom element
      if (element.tagName === 'DEFERRED-MEDIA') {
        if (element.loadContent && typeof element.loadContent === 'function') {
          element.loadContent(false);
        }
      } else {
        // Direct video element
        const src = element.dataset.src;
        if (src && !element.src) {
          element.src = src;
        }
      }

      element.setAttribute('data-loaded', 'true');
      if (element.parentElement) {
        element.parentElement.classList.remove('lazy-skeleton');
        element.parentElement.classList.add('lazy-loaded');
      }
    } catch (error) {
      console.error('Error loading video:', error);
    }
  }

  /**
   * Load image element with fade-in animation
   * Supports LQIP (Low Quality Image Placeholder) blur strategy
   */
  loadImage(element) {
    if (element.hasAttribute('data-loaded')) return;

    try {
      const srcset = element.dataset.srcset;
      const sizes = element.dataset.sizes || '100vw';
      const alt = element.dataset.alt || element.alt || '';

      if (srcset) {
        // Create new image for preloading
        const img = new Image();
        img.srcset = srcset;
        img.sizes = sizes;
        img.alt = alt;
        img.className = element.className;

        // Fade-in animation when loaded
        img.onload = () => {
          element.srcset = srcset;
          element.sizes = sizes;
          element.alt = alt;
          element.classList.remove('lazy-loading');
          element.classList.add('lazy-loaded');

          if (element.parentElement) {
            element.parentElement.classList.remove('lazy-skeleton');
            element.parentElement.classList.add('lazy-loaded');
          }
        };

        img.onerror = () => {
          console.warn('Failed to load image:', srcset);
          element.classList.add('lazy-error');
        };

        // Start loading
        img.src = srcset.split(',')[0].trim().split(' ')[0];
      }

      element.setAttribute('data-loaded', 'true');
    } catch (error) {
      console.error('Error loading image:', error);
    }
  }

  /**
   * Observe lazy-loading element
   * Call manually if element is dynamically added
   */
  observe(element) {
    if (this.observer) {
      this.observer.observe(element);
    }
  }

  /**
   * Observe multiple elements
   */
  observeAll(elements) {
    elements.forEach((element) => {
      this.observe(element);
    });
  }

  /**
   * Stop observing element
   */
  unobserve(element) {
    if (this.observer) {
      this.observer.unobserve(element);
    }
  }

  /**
   * Disconnect observer
   */
  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize LazyLoader on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const lazyLoader = new LazyLoader({
      rootMargin: '200px',
      threshold: 0.01
    });

    // Observe all lazy video elements
    const lazyVideos = document.querySelectorAll('[data-src]');
    lazyLoader.observeAll(lazyVideos);

    // Observe all lazy image elements
    const lazyImages = document.querySelectorAll('[data-srcset]');
    lazyLoader.observeAll(lazyImages);

    // Handle dynamically added lazy elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.dataset && (node.dataset.src || node.dataset.srcset)) {
              lazyLoader.observe(node);
            }
            // Check for lazy elements within added node
            const lazyElements = node.querySelectorAll?.[('[data-src], [data-srcset]')] || [];
            lazyLoader.observeAll(lazyElements);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
} else {
  // DOM already loaded
  const lazyLoader = new LazyLoader({
    rootMargin: '200px',
    threshold: 0.01
  });

  const lazyVideos = document.querySelectorAll('[data-src]');
  lazyLoader.observeAll(lazyVideos);

  const lazyImages = document.querySelectorAll('[data-srcset]');
  lazyLoader.observeAll(lazyImages);
}
